import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Lock, LogOut, Mail, MessageCircle, RefreshCw } from "lucide-react";
import { logo, CONTACT } from "@/data/content";
import AdminTools from "@/components/AdminTools";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STATUSES = ["new", "reviewed", "contacted", "archived"];

const STATUS_STYLES = {
  new: "bg-gold/15 text-gold-deep border-gold/40",
  reviewed: "bg-blue-500/15 text-blue-600 border-blue-500/40",
  contacted: "bg-emerald-500/15 text-emerald-600 border-emerald-500/40",
  archived: "bg-slate-500/15 text-slate-600 border-slate-500/40",
};

const SECTOR_NAMES = {
  ag: "Agriculture",
  edu: "Education",
  re: "Real Estate",
  ind: "Industrial Development",
  inf: "Infrastructure",
  aut: "Automation",
  avi: "Aviation",
};

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

const formatDate = (iso) => {
  try {
    return new Date(iso).toLocaleString("en-CA", { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return iso;
  }
};

const Login = ({ onSuccess }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password }, { withCredentials: true });
      toast.success("Welcome back.");
      onSuccess(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-white flex items-center justify-center px-4">
      <motion.form
        onSubmit={submit}
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        className="card-navy rounded-3xl p-8 sm:p-10 w-full max-w-md"
        data-testid="admin-login-form"
      >
        <img src={logo} alt="Pacific Investment Group" className="h-14 w-14 rounded-xl object-cover ring-1 ring-gold/40" />
        <h1 className="mt-6 font-display text-3xl text-navy-900">Admin Dashboard</h1>
        <p className="mt-2 text-sm text-slate-600">Sign in to review funding enquiries.</p>

        <div className="mt-8 space-y-5">
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-2 tracking-wide uppercase" htmlFor="admin-email">Email</label>
            <input
              id="admin-email"
              data-testid="admin-login-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="input-navy w-full rounded-xl px-4 py-3 text-sm"
              placeholder="admin@pacificinvestment.org"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-2 tracking-wide uppercase" htmlFor="admin-password">Password</label>
            <input
              id="admin-password"
              data-testid="admin-login-password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="input-navy w-full rounded-xl px-4 py-3 text-sm"
              placeholder="••••••••••"
            />
          </div>
        </div>

        <button
          type="submit"
          data-testid="admin-login-submit"
          disabled={busy}
          className="btn-gold mt-8 inline-flex w-full items-center justify-center gap-2 rounded-full px-8 py-3.5 text-sm font-semibold disabled:opacity-60"
        >
          <Lock size={15} strokeWidth={2} />
          {busy ? "Signing in…" : "Sign In"}
        </button>
      </motion.form>
    </div>
  );
};

const Dashboard = ({ user, onLogout }) => {
  const [enquiries, setEnquiries] = useState([]);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/enquiries`, { withCredentials: true });
      setEnquiries(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const setStatus = async (id, status) => {
    try {
      const { data } = await axios.patch(`${API}/admin/enquiries/${id}/status`, { status }, { withCredentials: true });
      setEnquiries((prev) => prev.map((e) => (e.id === id ? data : e)));
      toast.success(`Marked as ${status}.`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const filtered = filter === "all" ? enquiries : enquiries.filter((e) => (e.status || "new") === filter);
  const counts = { all: enquiries.length };
  STATUSES.forEach((s) => (counts[s] = enquiries.filter((e) => (e.status || "new") === s).length));

  return (
    <div className="min-h-screen bg-white text-navy-900">
      <header className="glass sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <img src={logo} alt="Pacific Investment Group" className="h-10 w-10 rounded-lg object-cover ring-1 ring-gold/40" />
            <div className="leading-tight">
              <p className="font-display text-lg text-navy-900">Admin Dashboard</p>
              <p className="text-[10px] font-mono-brand tracking-[0.25em] text-gold-deep uppercase">{user?.email}</p>
            </div>
          </div>
          <div className="flex items-center gap-2.5">
            <AdminTools />
            <button
              data-testid="admin-refresh-button"
              onClick={load}
              className="btn-ghost-gold inline-flex items-center gap-2 rounded-full px-4 py-2 text-xs"
            >
              <RefreshCw size={13} className={loading ? "animate-spin" : ""} />
              Refresh
            </button>
            <button
              data-testid="admin-logout-button"
              onClick={onLogout}
              className="btn-ghost-gold inline-flex items-center gap-2 rounded-full px-4 py-2 text-xs"
            >
              <LogOut size={13} />
              Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-10">
        <div className="flex flex-wrap gap-2.5">
          {["all", ...STATUSES].map((s) => (
            <button
              key={s}
              data-testid={`admin-filter-${s}`}
              onClick={() => setFilter(s)}
              className={`rounded-full px-4 py-2 text-xs capitalize border transition-all duration-300 ${
                filter === s ? "btn-gold border-transparent font-semibold" : "border-gold/25 text-slate-700 hover:border-gold/60"
              }`}
            >
              {s} ({counts[s] ?? 0})
            </button>
          ))}
        </div>

        {filtered.length === 0 && !loading && (
          <div className="mt-14 rounded-3xl card-navy p-14 text-center">
            <p className="font-display text-2xl text-slate-700">No enquiries here yet.</p>
            <p className="mt-2 text-sm text-slate-500">New enquiries from the website will appear in this list.</p>
          </div>
        )}

        <div className="mt-8 space-y-4">
          {filtered.map((e, i) => {
            const digits = (e.phone || "").replace(/[^\d]/g, "");
            return (
              <motion.article
                key={e.id}
                data-testid={`admin-enquiry-card-${i}`}
                initial={{ opacity: 0, y: 18 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: Math.min(i, 8) * 0.05, ease: [0.22, 1, 0.36, 1] }}
                className="card-navy rounded-3xl p-6 sm:p-8"
              >
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div>
                    <div className="flex flex-wrap items-center gap-3">
                      <h3 className="font-display text-2xl text-navy-900">{e.name}</h3>
                      <span className={`rounded-full border px-3 py-1 text-[11px] uppercase tracking-wider ${STATUS_STYLES[e.status || "new"]}`} data-testid={`admin-enquiry-status-${i}`}>
                        {e.status || "new"}
                      </span>
                    </div>
                    <p className="mt-1.5 text-sm text-slate-600">
                      {formatDate(e.created_at)} · {e.service}{e.sector ? ` · ${SECTOR_NAMES[e.sector] || e.sector}` : ""}{e.amount ? ` · ${e.amount}` : ""}
                    </p>
                  </div>
                  <select
                    data-testid={`admin-enquiry-status-select-${i}`}
                    value={e.status || "new"}
                    onChange={(ev) => setStatus(e.id, ev.target.value)}
                    className="input-navy rounded-full px-4 py-2 text-xs"
                    aria-label="Update status"
                  >
                    {STATUSES.map((s) => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                </div>

                {e.message && (
                  <p className="mt-4 rounded-2xl bg-[#F8F7F0] border border-gold/20 p-4 text-sm leading-relaxed text-slate-700">
                    “{e.message}”
                  </p>
                )}

                <div className="mt-5 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-slate-600">
                  <a href={`mailto:${e.email}`} data-testid={`admin-enquiry-email-${i}`} className="inline-flex items-center gap-2 hover:text-gold-deep transition-colors duration-300">
                    <Mail size={14} className="text-gold-deep" /> {e.email}
                  </a>
                  {e.company && <span>Company: {e.company}</span>}
                  {digits && (
                    <a
                      href={`https://wa.me/${digits}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`admin-enquiry-whatsapp-${i}`}
                      className="inline-flex items-center gap-2 hover:text-emerald-600 transition-colors duration-300"
                    >
                      <MessageCircle size={14} className="text-emerald-400" /> {e.phone || digits}
                    </a>
                  )}
                </div>
              </motion.article>
            );
          })}
        </div>

        <p className="mt-12 text-center text-xs text-slate-600">
          Owner inbox for alerts: {CONTACT.email}
        </p>
      </main>
    </div>
  );
};

export default function Admin() {
  const [user, setUser] = useState(undefined);

  useEffect(() => {
    window.scrollTo(0, 0);
    axios
      .get(`${API}/auth/me`, { withCredentials: true })
      .then(({ data }) => setUser(data))
      .catch(() => setUser(null));
  }, []);

  if (user === undefined) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="h-10 w-10 rounded-full border-2 border-gold border-t-transparent animate-spin" />
      </div>
    );
  }

  if (!user) {
    return (
      <Login
        onSuccess={(u) => setUser(u)}
      />
    );
  }

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch {}
    setUser(null);
    toast.success("Signed out.");
  };

  return <Dashboard user={user} onLogout={logout} />;
}
